import { supabase } from './supabase';
import { logger, LogCategory } from './logger';
/**
 * Service for interacting with the knowledge_base_ADVISORI table
 */
export class KnowledgebaseService {
    constructor() {
        Object.defineProperty(this, "TABLE_NAME", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'knowledge_base_ADVISORI'
        });
    }
    /**
     * Get all entries from the knowledge_base_ADVISORI table
     * @returns Promise resolving to an array of KnowledgebaseEntry objects
     */
    async getEntries() {
        try {
            logger.info('Fetching all knowledgebase entries', LogCategory.DATABASE);
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('*')
                .order('topic', { ascending: true })
                .order('subtopic', { ascending: true });
            if (error) {
                logger.error('Error fetching knowledgebase entries', LogCategory.DATABASE, { error });
                throw error;
            }
            logger.info(`Retrieved ${data?.length || 0} knowledgebase entries`, LogCategory.DATABASE);
            return data || [];
        }
        catch (error) {
            logger.error('Exception in getEntries', LogCategory.DATABASE, { error });
            throw error;
        }
    }
    /**
     * Get a specific entry by ID
     * @param id The ID of the entry to retrieve
     * @returns Promise resolving to a KnowledgebaseEntry object
     */
    async getEntry(id) {
        try {
            logger.info('Fetching knowledgebase entry by ID', LogCategory.DATABASE, { id });
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('*')
                .eq('id', id)
                .single();
            if (error) {
                logger.error('Error fetching knowledgebase entry', LogCategory.DATABASE, { error, id });
                throw error;
            }
            return data;
        }
        catch (error) {
            logger.error('Exception in getEntry', LogCategory.DATABASE, { error, id });
            throw error;
        }
    }
    /**
     * Create a new entry in the knowledge_base_ADVISORI table
     * @param entry The entry to create
     * @returns Promise resolving to the created entry
     */
    async createEntry(entry) {
        try {
            logger.info('Creating new knowledgebase entry', LogCategory.DATABASE, {
                topic: entry.topic,
                subtopic: entry.subtopic
            });
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .insert([entry])
                .select()
                .single();
            if (error) {
                logger.error('Error creating knowledgebase entry', LogCategory.DATABASE, { error, entry });
                throw error;
            }
            logger.info('Successfully created knowledgebase entry', LogCategory.DATABASE, {
                id: data.id,
                topic: data.topic,
                subtopic: data.subtopic
            });
            return data;
        }
        catch (error) {
            logger.error('Exception in createEntry', LogCategory.DATABASE, { error, entry });
            throw error;
        }
    }
    /**
     * Update an existing entry in the knowledge_base_ADVISORI table
     * @param id The ID of the entry to update
     * @param entry The updated entry data
     * @returns Promise resolving to the updated entry
     */
    async updateEntry(id, entry) {
        try {
            logger.info('Updating knowledgebase entry', LogCategory.DATABASE, {
                id,
                topic: entry.topic,
                subtopic: entry.subtopic
            });
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .update(entry)
                .eq('id', id)
                .select()
                .single();
            if (error) {
                logger.error('Error updating knowledgebase entry', LogCategory.DATABASE, { error, id, entry });
                throw error;
            }
            logger.info('Successfully updated knowledgebase entry', LogCategory.DATABASE, {
                id,
                topic: data.topic,
                subtopic: data.subtopic
            });
            return data;
        }
        catch (error) {
            logger.error('Exception in updateEntry', LogCategory.DATABASE, { error, id, entry });
            throw error;
        }
    }
    /**
     * Delete an entry from the knowledge_base_ADVISORI table
     * @param id The ID of the entry to delete
     * @returns Promise resolving to true if successful
     */
    async deleteEntry(id) {
        try {
            logger.info('Deleting knowledgebase entry', LogCategory.DATABASE, { id });
            const { error } = await supabase
                .from(this.TABLE_NAME)
                .delete()
                .eq('id', id);
            if (error) {
                logger.error('Error deleting knowledgebase entry', LogCategory.DATABASE, { error, id });
                throw error;
            }
            logger.info('Successfully deleted knowledgebase entry', LogCategory.DATABASE, { id });
            return true;
        }
        catch (error) {
            logger.error('Exception in deleteEntry', LogCategory.DATABASE, { error, id });
            throw error;
        }
    }
    /**
     * Search for entries in the knowledge_base_ADVISORI table
     * @param searchTerm The term to search for in topic, subtopic, and description
     * @returns Promise resolving to an array of matching entries
     */
    async searchEntries(searchTerm) {
        try {
            logger.info('Searching knowledgebase entries', LogCategory.DATABASE, { searchTerm });
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('*')
                .or(`topic.ilike.%${searchTerm}%,subtopic.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
                .order('topic', { ascending: true })
                .order('subtopic', { ascending: true });
            if (error) {
                logger.error('Error searching knowledgebase entries', LogCategory.DATABASE, { error, searchTerm });
                throw error;
            }
            logger.info(`Found ${data?.length || 0} matching knowledgebase entries`, LogCategory.DATABASE, { searchTerm });
            return data || [];
        }
        catch (error) {
            logger.error('Exception in searchEntries', LogCategory.DATABASE, { error, searchTerm });
            throw error;
        }
    }
    /**
     * Parse a description string with bulletpoints into an array of strings
     * @param description The description string with bulletpoints
     * @returns Array of bulletpoint strings
     */
    parseBulletpoints(description) {
        if (!description)
            return [];
        // Split by newline and filter out empty lines
        const lines = description.split('\n').filter(line => line.trim() !== '');
        // Process each line to remove the leading dash if present
        return lines.map(line => {
            const trimmed = line.trim();
            if (trimmed.startsWith('-')) {
                return trimmed.substring(1).trim();
            }
            return trimmed;
        });
    }
    /**
     * Format an array of bulletpoints into a description string
     * @param bulletpoints Array of bulletpoint strings
     * @returns Formatted description string
     */
    formatBulletpoints(bulletpoints) {
        if (!bulletpoints || bulletpoints.length === 0)
            return '';
        // Add a leading dash to each line if not already present
        return bulletpoints
            .map(point => {
            const trimmed = point.trim();
            if (trimmed === '')
                return '';
            if (trimmed.startsWith('-'))
                return trimmed;
            return `- ${trimmed}`;
        })
            .filter(point => point !== '')
            .join('\n');
    }
}
